import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { StyledDateRange, YearMonth } from "models/propTypes/commonPropTypes";
import { User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { SalaryDeviationListViewProps } from "models/propTypes/salaryDeviationPropTypes";
import { SalaryDeviationConstants } from "utils/constants";
//#endregion
//#region components
import { TableAsync } from "components/common";
import useCreateLeaveDialog from "./forms/useCreateLeaveDialog";
import useCreateAllowanceDialog from "./forms/useCreateAllowanceDialog";
import LockDeviationIcon from "./lockDeviationIcon";
import { useSalaryDeviationsService } from "serviceHooks";
//#endregions
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";
import { SalaryDeviationsService } from "services";

const SalaryDeviationListView: React.FC<SalaryDeviationListViewProps> = (props) => {
  const salaryDeviationsService = useSalaryDeviationsService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const {
    filter,
    reloadKey,
    reloadList,
    includePersonsWithNoDeviations,
    deviationMonth,
    tableId,
    disableQueryPagination,
  } = props;

  const [hasLockedRows, setHasLockedRows] = React.useState<boolean>();

  const filterFunc = React.useMemo(() => {
    return filter
      ? (baseFilter: WintUtilsWebApi.Filter) => {
          return salaryDeviationsService
            .reportFilter(baseFilter, filter, includePersonsWithNoDeviations)
            .then((res) => {
              setHasLockedRows(
                res.data.items?.some((i) => salaryDeviationsService.isMonthDisabled(i.monthState)) ?? false
              );
              props.onChangeTotalItems && props.onChangeTotalItems(res.data.totalItems);
              return res;
            });
        }
      : [];
  }, [filter]);

  const openLeaveDialog = useCreateLeaveDialog(reloadList);
  const openAllowanceDialog = useCreateAllowanceDialog(reloadList);

  const onRowClicked = (row: WintSuperkollAPIModel.SalaryDeviationDescriptor, rowIndex: number) => {
    if (!row.id) return;
    if (
      SalaryDeviationConstants.LeaveTypes.concat(SalaryDeviationConstants.TimeReportingDeviationTypes).some(
        (t) => t === row.deviationType
      )
    )
      dispatch(
        displayDialog(
          openLeaveDialog({
            salaryDeviationId: row.id,
            monthState: row.monthState,
            isAdminOnlyType: row.isAdminOnlyType,
          })
        )
      );
    else
      dispatch(
        displayDialog(
          openAllowanceDialog({
            salaryDeviationId: row.id,
            monthState: row.monthState,
            isAdminOnlyType: row.isAdminOnlyType,
          })
        )
      );
  };

  const isMonthDisabled = deviationMonth ? salaryDeviationsService.isMonthDisabled(deviationMonth.state) : undefined;

  return (
    <TableAsync<WintSuperkollAPIModel.SalaryDeviationDescriptor>
      tableId={tableId}
      properties={{
        monthState: {
          formatter: (deviation: WintSuperkollAPIModel.SalaryDeviationDescriptor) => {
            if (isMonthDisabled ?? salaryDeviationsService.isMonthDisabled(deviation.monthState))
              return <LockDeviationIcon />;
          },
          disableSort: true,
          alignment: "center",
          width: 24,
        },
        personName: {
          description: TranslationKeys.Common.Employee,
          formatter: (deviation: WintSuperkollAPIModel.SalaryDeviationDescriptor) =>
            new User(deviation.personId, deviation.personName),
          maxWidth: 150,
          noWrap: true,
        },
        deviationType: {
          description: TranslationKeys.SalaryDeviation.Type,
          formatter: (deviation: WintSuperkollAPIModel.SalaryDeviationDescriptor) =>
            deviation.hasDeviationsForPeriod
              ? SalaryDeviationsService.getSalaryDeviationTypeText(
                  deviation.deviationType,
                  deviation.customDeviationName
                )
              : "",
          noWrap: true,
          maxWidth: 100,
        },
        fromDate: {
          description: TranslationKeys.Common.TimePeriod,
          formatter: (deviation: WintSuperkollAPIModel.SalaryDeviationDescriptor) =>
            deviation.fromDate
              ? new StyledDateRange(deviation.fromDate, deviation.toDate)
              : new YearMonth(deviation.yearAndMonth),
        },
        comment: {
          description: TranslationKeys.Common.Comment,
          noWrap: true,
          maxWidth: 130,
        },
        summary: {
          description: TranslationKeys.Common.Details,
          formatter: (deviation: WintSuperkollAPIModel.SalaryDeviationDescriptor) =>
            salaryDeviationsService.getSalaryDeviationDetailsText(deviation.summary),
          alignment: "right",
          className: clsx(cssExports.textBlack),
          maxWidth: 80,
          disableSort: true,
        },
        group: includePersonsWithNoDeviations
          ? {
              formatter: (deviation: WintSuperkollAPIModel.SalaryDeviationDescriptor) => {
                if (deviation.hasDeviationsForPeriod)
                  return LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.SalaryDeviation.SubmittedDeviations
                  );
                else
                  return LocalizationHelperInstance.getFormattedMessage(TranslationKeys.SalaryDeviation.NoDeviations);
              },
            }
          : undefined,
      }}
      rowClickHandler={onRowClicked}
      filter={filterFunc}
      maxRowCount={10}
      pagination="advanced"
      reloadKey={reloadKey}
      hiddenColumns={isMonthDisabled || hasLockedRows ? undefined : ["monthState"]}
      rowHoverActionsHandler={props.rowHoverActionsHandler}
      orderByProperty={props.orderByProperty}
      orderByDescending={props.orderByDescending}
      thenByProperty={props.thenByProperty}
      disableQueryPagination={disableQueryPagination}
    />
  );
};
export default SalaryDeviationListView;
